/**
 * Judgment System
 *
 * Handles hit detection and judgment calculation
 */

import { Note } from './Note';
import { InputEvent } from '../engine/InputManager';

export type JudgmentType = 'PERFECT' | 'GREAT' | 'GOOD' | 'MISS';

export interface Judgment {
  type: JudgmentType;
  timing: number;      // How early (-) or late (+) in milliseconds
  note: Note;
  score: number;
}

export class JudgmentSystem {
  // Timing windows in milliseconds
  private readonly WINDOWS = {
    PERFECT: 50,   // ±50ms for perfect
    GREAT: 100,    // ±100ms for great
    GOOD: 150,     // ±150ms for good
    MISS: 180      // Notes auto-miss if they pass 180ms beyond the judgment line
  };

  // Points for each judgment
  private readonly SCORES = {
    PERFECT: 100,
    GREAT: 75,
    GOOD: 50,
    MISS: 0
  };

  /**
   * Check if an input hits a note and calculate judgment
   */
  checkHit(input: InputEvent, notes: Note[], currentTime: number): Judgment | null {
    // Filter notes in the same lane that haven't been hit
    const laneNotes = notes.filter(
      note => note.lane === input.lane && !note.hit && !note.missed
    );

    if (laneNotes.length === 0) return null;

    // Find closest note within miss window
    // Use currentTime (game time) instead of input.timestamp (performance.now)
    let closestNote: Note | null = null;
    let minDiff = Infinity;

    for (const note of laneNotes) {
      const diff = Math.abs(note.time - currentTime);
      if (diff < minDiff && diff <= this.WINDOWS.MISS) {
        minDiff = diff;
        closestNote = note;
      }
    }

    if (!closestNote) return null;

    // Calculate timing difference (negative = early, positive = late)
    // Compare note time with current game time
    const timing = currentTime - closestNote.time;
    const absTiming = Math.abs(timing);

    // Determine judgment type
    let judgmentType: JudgmentType;
    if (absTiming <= this.WINDOWS.PERFECT) {
      judgmentType = 'PERFECT';
    } else if (absTiming <= this.WINDOWS.GREAT) {
      judgmentType = 'GREAT';
    } else if (absTiming <= this.WINDOWS.GOOD) {
      judgmentType = 'GOOD';
    } else {
      judgmentType = 'MISS';
    }

    // Mark note as hit
    closestNote.markHit();

    return {
      type: judgmentType,
      timing: timing,
      note: closestNote,
      score: this.SCORES[judgmentType]
    };
  }

  /**
   * Auto-miss notes that are too far past the judgment line
   */
  checkMisses(notes: Note[], currentTime: number): Judgment[] {
    const missedJudgments: Judgment[] = [];

    for (const note of notes) {
      if (note.hit || note.missed) continue;

      const timePassed = currentTime - note.time;
      if (timePassed > this.WINDOWS.MISS) {
        note.markMissed();

        missedJudgments.push({
          type: 'MISS',
          timing: timePassed,
          note: note,
          score: 0
        });
      }
    }

    return missedJudgments;
  }

  /**
   * Get timing windows (for display/configuration)
   */
  getWindows() {
    return { ...this.WINDOWS };
  }

  /**
   * Get score values (for display/configuration)
   */
  getScores() {
    return { ...this.SCORES };
  }
}
